import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Staff, StaffRole } from '@/lib/types';
import {
  Banner,
  Card,
  Field,
  Modal,
  ModalHeader,
  PageHeader,
  Pill,
  Stat,
  Tag,
  cellStyle,
  inputStyle,
  thStyle,
  useToast,
} from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { useSort, SortTh } from '@/components/sortable';
import { SearchInput, matches } from '@/components/SearchInput';
import { useAuth } from '@/store/auth';

const ROLES: { value: StaffRole; label: string }[] = [
  { value: 'TENANT_ADMIN', label: '院長 / 老闆' },
  { value: 'STORE_MANAGER', label: '店長' },
  { value: 'DOCTOR', label: '醫師' },
  { value: 'CONSULTANT', label: '諮詢師' },
  { value: 'ESTHETICIAN', label: '美容師' },
  { value: 'FRONT_DESK', label: '櫃檯' },
];

export function StaffPage() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === 'TENANT_ADMIN';
  const { msg, ok, fail } = useToast();
  const [addOpen, setAddOpen] = useState(false);
  const [q, setQ] = useState('');
  const [roleFilter, setRoleFilter] = useState('');

  const staff = useQuery({ queryKey: ['staff'], queryFn: () => api.get<Staff[]>('/catalog/staff') });
  const allStaff = staff.data ?? [];
  const list = allStaff.filter(
    (s) =>
      (!q || matches(s.name, q) || matches(s.email, q)) &&
      (!roleFilter || s.role === roleFilter),
  );

  const refresh = () => void qc.invalidateQueries({ queryKey: ['staff'] });

  const update = useMutation({
    mutationFn: (v: { id: string; data: Partial<Pick<Staff, 'role' | 'status'>> }) => api.patch(`/catalog/staff/${v.id}`, v.data),
    onSuccess: () => { ok('員工資料已更新'); refresh(); },
    onError: fail,
  });

  const sort = useSort(list, {
    name: (s) => s.name,
    email: (s) => s.email,
    role: (s) => s.role,
    status: (s) => s.status,
  });

  if (!isAdmin) {
    return (
      <div>
        <PageHeader title="員工 / 權限管理" subtitle="帳號 · 角色 · 啟用狀態" />
        <Card title="權限不足"><div style={{ padding: 12, color: 'var(--ink-3)', fontSize: 13 }}>僅租戶管理者（TENANT_ADMIN）可管理員工帳號與權限。</div></Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="員工 / 權限管理"
        subtitle="帳號 · 角色 · 專長 · 啟用狀態（密碼以 argon2 雜湊，永不外洩）"
        right={<button className="btn btn-primary" onClick={() => setAddOpen(true)}><Icon d={I.plus} size={13} /> 新增員工</button>}
      />
      <Banner msg={msg} />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="員工總數" value={String(allStaff.length)} tone="primary" />
        <Stat label="在職" value={String(allStaff.filter((s) => s.status === 'active').length)} tone="success" />
        <Stat label="停用" value={String(allStaff.filter((s) => s.status === 'disabled').length)} tone="danger" />
        <Stat label="醫師人數" value={String(allStaff.filter((s) => s.role === 'DOCTOR').length)} tone="ink" />
      </div>

      <Card
        title="員工名冊"
        icon={<Icon d={I.users} size={14} style={{ color: 'var(--primary)' }} />}
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <SearchInput value={q} onChange={setQ} placeholder="搜尋姓名 / Email" />
            <div style={{ width: 130 }}>
              <Select value={roleFilter} onChange={setRoleFilter} placeholder="全部角色" options={[{ value: '', label: '全部角色' }, ...ROLES]} />
            </div>
          </div>
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortTh label="姓名" sortKey="name" sort={sort} />
              <SortTh label="Email" sortKey="email" sort={sort} />
              <SortTh label="角色" sortKey="role" sort={sort} />
              <th style={thStyle}>專長</th>
              <SortTh label="狀態" sortKey="status" sort={sort} />
              <th style={{ ...thStyle, textAlign: 'right' }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((s) => (
              <tr key={s.id}>
                <td style={cellStyle}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 10, height: 10, borderRadius: 99, background: s.color, display: 'inline-block' }} />
                    <b>{s.name}</b>
                  </span>
                </td>
                <td style={{ ...cellStyle, color: 'var(--ink-2)' }}>{s.email}</td>
                <td style={cellStyle}>
                  <div style={{ width: 130 }}>
                    <Select
                      value={s.role}
                      onChange={(v) => update.mutate({ id: s.id, data: { role: v as StaffRole } })}
                      options={ROLES}
                    />
                  </div>
                </td>
                <td style={cellStyle}>{s.specialties.length ? s.specialties.map((sp) => <Tag key={sp} tone="tag" style={{ marginRight: 4 }}>{sp}</Tag>) : <span style={{ color: 'var(--ink-4)' }}>—</span>}</td>
                <td style={cellStyle}>{s.status === 'active' ? <Pill bg="var(--success-bg)" color="var(--success)">在職</Pill> : <Pill bg="var(--danger-bg)" color="var(--danger)">停用</Pill>}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }}>
                  <button
                    className="btn btn-ghost"
                    style={{ fontSize: 11 }}
                    disabled={s.id === user?.userId}
                    title={s.id === user?.userId ? '無法停用自己' : ''}
                    onClick={() => update.mutate({ id: s.id, data: { status: s.status === 'active' ? 'disabled' : 'active' } })}
                  >
                    {s.status === 'active' ? '停用' : '啟用'}
                  </button>
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={6}>尚無員工</td></tr>}
          </tbody>
        </table>
      </Card>

      {addOpen && (
        <AddStaffModal
          onClose={() => setAddOpen(false)}
          onSaved={() => { ok('員工已新增'); setAddOpen(false); refresh(); }}
          onFail={fail}
        />
      )}
    </div>
  );
}

function AddStaffModal({ onClose, onSaved, onFail }: { onClose: () => void; onSaved: () => void; onFail: (e: unknown) => void }) {
  const [f, setF] = useState({ name: '', email: '', password: '', role: 'FRONT_DESK' as StaffRole, specialties: '', color: '#B97A65' });
  const set = (k: keyof typeof f, v: string) => setF((s) => ({ ...s, [k]: v }));

  const create = useMutation({
    mutationFn: () =>
      api.post('/catalog/staff', {
        name: f.name,
        email: f.email,
        password: f.password,
        role: f.role,
        specialties: f.specialties ? f.specialties.split(/[,，、]/).map((x) => x.trim()).filter(Boolean) : [],
        color: f.color,
      }),
    onSuccess: onSaved,
    onError: onFail,
  });

  const ready = f.name && /.+@.+\..+/.test(f.email) && f.password.length >= 8;

  return (
    <Modal open onClose={onClose} width={520}>
      <ModalHeader title="新增員工" sub="密碼至少 8 碼，將以 argon2 雜湊儲存" onClose={onClose} />
      <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="姓名 *"><input style={inputStyle} value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="王小明" /></Field>
          <Field label="角色 *"><Select value={f.role} onChange={(v) => set('role', v)} options={ROLES} /></Field>
        </div>
        <Field label="Email *"><input style={inputStyle} value={f.email} onChange={(e) => set('email', e.target.value)} placeholder="doctor@liufu.tw" autoComplete="off" /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label="密碼 *（≥8 碼）"><input type="password" style={inputStyle} value={f.password} onChange={(e) => set('password', e.target.value)} autoComplete="new-password" /></Field>
          <Field label="代表色"><input type="color" style={{ ...inputStyle, padding: 2, height: 38 }} value={f.color} onChange={(e) => set('color', e.target.value)} /></Field>
        </div>
        <Field label="專長（逗號分隔，選填）"><input style={inputStyle} value={f.specialties} onChange={(e) => set('specialties', e.target.value)} placeholder="雷射, 微整, 諮詢" /></Field>
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>取消</button>
        <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!ready || create.isPending} onClick={() => create.mutate()}>建立員工</button>
      </div>
    </Modal>
  );
}
